import React, { useState } from "react";
import { diagnosticQuestions } from "./questions";
import { DiagnosticResult } from "./DiagnosticQuiz";
import {
  Visibility,
  Build,
  Security,
  ArrowForward,
  CheckCircle,
  TrendingUp,
  Lightbulb,
  Groups,
} from "@mui/icons-material";

interface DiagnosticQuizContainerProps {
  onQuizComplete: (results: DiagnosticResult[]) => void;
}

const OPTION_LABELS = ["A", "B", "C", "D"];

const QUESTION_CONFIG = [
  { icon: <Groups />, category: "Leadership Alignment" },
  { icon: <TrendingUp />, category: "Initiative Execution" },
  { icon: <Lightbulb />, category: "Resource Allocation" },
  { icon: <Security />, category: "Change Sustainment" },
  { icon: <Visibility />, category: "Progress Measurement" },
];

const DiagnosticQuizContainer: React.FC<DiagnosticQuizContainerProps> = ({
  onQuizComplete,
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const question = diagnosticQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / diagnosticQuestions.length) * 100;
  const totalQuestions = diagnosticQuestions.length;
  const config = QUESTION_CONFIG[currentQuestion] || QUESTION_CONFIG[0];

  const handleOptionSelect = (optionIndex: number) => {
    if (!isTransitioning) setSelectedOption(optionIndex);
  };

  const handleNext = () => {
    if (selectedOption === null || isTransitioning) return;

    const newResult: DiagnosticResult = {
      questionId: question.id,
      selectedOptionIndex: selectedOption,
      question: question.question,
      answer: question.options[selectedOption].text,
      scores: question.options[selectedOption].scores,
    };

    const updatedResults = [...results, newResult];
    setResults(updatedResults);

    if (currentQuestion < diagnosticQuestions.length - 1) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
        setIsTransitioning(false);
      }, 280);
    } else {
      onQuizComplete(updatedResults);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <div
        style={{
          position: "absolute",
          top: "-120px",
          right: "-120px",
          width: "450px",
          height: "450px",
          borderRadius: "50%",
          background: "rgba(201, 167, 80, 0.08)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-100px",
          left: "-100px",
          width: "350px",
          height: "350px",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.05)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          backgroundColor: "white",
          borderRadius: "24px",
          padding: "3rem",
          maxWidth: "760px",
          width: "100%",
          boxShadow: "0 25px 80px rgba(0, 0, 0, 0.35)",
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(10px)" : "translateY(0)",
          transition: "opacity 0.28s ease, transform 0.28s ease",
        }}
      >
        {/* Progress Section */}
        <div style={{ marginBottom: "2.5rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.75rem",
            }}
          >
            <span
              style={{
                fontSize: "0.85rem",
                fontWeight: "600",
                color: "#152A53",
              }}
            >
              Question {currentQuestion + 1} of {totalQuestions}
            </span>
            <span
              style={{
                fontSize: "0.85rem",
                fontWeight: "700",
                color: "#c9a750",
              }}
            >
              {Math.round(progress)}% Complete
            </span>
          </div>

          <div
            style={{
              width: "100%",
              height: "6px",
              backgroundColor: "#edf2f7",
              borderRadius: "3px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                background: "linear-gradient(90deg, #c9a750, #e4c070)",
                borderRadius: "3px",
                transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>

          {/* Step dot indicators */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginTop: "0.875rem",
            }}
          >
            {Array.from({ length: totalQuestions }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === currentQuestion ? "24px" : "8px",
                  height: "8px",
                  borderRadius: "4px",
                  backgroundColor:
                    i < currentQuestion
                      ? "#c9a750"
                      : i === currentQuestion
                        ? "#152A53"
                        : "#e2e8f0",
                  transition: "all 0.35s ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Question Header */}
        <div style={{ marginBottom: "2rem", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #c9a750, #e4c070)",
              color: "white",
              marginBottom: "1.25rem",
              boxShadow: "0 8px 20px rgba(201, 167, 80, 0.4)",
            }}
          >
            {config.icon}
          </div>

          <div>
            <div
              style={{
                display: "inline-block",
                backgroundColor: "#fefce8",
                border: "1px solid #e4d6a7",
                borderRadius: "20px",
                padding: "0.25rem 0.875rem",
                fontSize: "0.7rem",
                fontWeight: "700",
                color: "#b8923d",
                marginBottom: "0.875rem",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {config.category}
            </div>
          </div>

          <h2
            style={{
              fontSize: "1.35rem",
              fontWeight: "700",
              color: "#1a202c",
              lineHeight: "1.45",
              margin: "0 0 0.5rem 0",
            }}
          >
            {question.question}
          </h2>
          <p style={{ fontSize: "0.8rem", color: "#a0aec0", margin: 0 }}>
            Select the option that best describes your current situation
          </p>
        </div>

        {/* Options */}
        <div style={{ marginBottom: "2rem" }}>
          {question.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionSelect(index)}
              style={{
                width: "100%",
                padding: "1rem 1.25rem",
                marginBottom: "0.75rem",
                fontSize: "0.95rem",
                fontWeight: "500",
                color: selectedOption === index ? "#152A53" : "#4a5568",
                backgroundColor:
                  selectedOption === index ? "#fefce8" : "#f8fafc",
                border:
                  selectedOption === index
                    ? "2px solid #c9a750"
                    : "2px solid #e2e8f0",
                borderRadius: "12px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                boxShadow:
                  selectedOption === index
                    ? "0 4px 14px rgba(201, 167, 80, 0.2)"
                    : "none",
              }}
              onMouseEnter={(e) => {
                if (selectedOption !== index) {
                  e.currentTarget.style.backgroundColor = "#fefce8";
                  e.currentTarget.style.borderColor = "#e4d6a7";
                  e.currentTarget.style.transform = "translateX(4px)";
                }
              }}
              onMouseLeave={(e) => {
                if (selectedOption !== index) {
                  e.currentTarget.style.backgroundColor = "#f8fafc";
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.transform = "translateX(0)";
                }
              }}
            >
              {/* Letter badge */}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "30px",
                  height: "30px",
                  borderRadius: "50%",
                  backgroundColor:
                    selectedOption === index ? "#c9a750" : "#e8edf2",
                  color: selectedOption === index ? "white" : "#718096",
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  flexShrink: 0,
                  transition: "all 0.2s",
                }}
              >
                {OPTION_LABELS[index]}
              </span>
              <span style={{ flex: 1, lineHeight: "1.5" }}>{option.text}</span>
              {selectedOption === index && (
                <CheckCircle
                  style={{
                    color: "#c9a750",
                    fontSize: "1.35rem",
                    flexShrink: 0,
                  }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={selectedOption === null}
          style={{
            width: "100%",
            padding: "1rem 2rem",
            fontSize: "1.05rem",
            fontWeight: "700",
            color: "white",
            background:
              selectedOption !== null
                ? "linear-gradient(135deg, #c9a750, #b8923d)"
                : "#cbd5e0",
            border: "none",
            borderRadius: "12px",
            cursor: selectedOption !== null ? "pointer" : "not-allowed",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
            boxShadow:
              selectedOption !== null
                ? "0 4px 18px rgba(201, 167, 80, 0.45)"
                : "none",
            letterSpacing: "0.025em",
          }}
          onMouseEnter={(e) => {
            if (selectedOption !== null) {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 8px 25px rgba(201, 167, 80, 0.55)";
            }
          }}
          onMouseLeave={(e) => {
            if (selectedOption !== null) {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 4px 18px rgba(201, 167, 80, 0.45)";
            }
          }}
        >
          {currentQuestion < diagnosticQuestions.length - 1
            ? "Next Question"
            : "See My Results"}
          <ArrowForward />
        </button>

        <p
          style={{
            textAlign: "center",
            fontSize: "0.75rem",
            color: "#a0aec0",
            marginTop: "1rem",
          }}
        >
          Your responses help us identify your biggest transformation obstacle
        </p>
      </div>
    </div>
  );
};

export default DiagnosticQuizContainer;
